import React, { useState, useEffect, useCallback } from 'react';
import { Play, Pause, ChevronLeft, ChevronRight, Settings, Maximize2, SkipForward, SkipBack } from 'lucide-react';

const Slider = ({ tenant = 'choice' }) => {
  // ===== STATE MANAGEMENT =====
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [loading, setLoading] = useState(true);
  const [interval, setIntervalDuration] = useState(5000);
  const [showSettings, setShowSettings] = useState(false);
  const [transitionStyle, setTransitionStyle] = useState('fade');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [viewCounts, setViewCounts] = useState({});

  // ===== FETCH SLIDES WITH ERROR HANDLING =====
  const fetchSlides = useCallback(async () => {
    try {
      const response = await fetch(`/tenant-content/${tenant}/slides.json`);
      if (!response.ok) throw new Error('Failed to fetch slides');
      
      const data = await response.json();
      setSlides(data.slides || []);
      setIsPlaying(data.autoplay !== undefined ? data.autoplay : true);
      if (data.interval) setIntervalDuration(data.interval);
      
      // Initialize view counts from localStorage
      const savedCounts = localStorage.getItem(`slider_views_${tenant}`);
      if (savedCounts) {
        setViewCounts(JSON.parse(savedCounts));
      }
    } catch (error) {
      console.error('Failed to load slides:', error);
      setSlides([]);
    } finally {
      setLoading(false);
    }
  }, [tenant]);

  useEffect(() => {
    fetchSlides();
  }, [fetchSlides]);

  // ===== NAVIGATION FUNCTIONS (wrapped in useCallback) =====
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setIsPlaying(false);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setIsPlaying(false);
  }, [slides.length]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
    setIsPlaying(false);
  }, []);

  const goToFirst = useCallback(() => {
    setCurrentSlide(0);
    setIsPlaying(false);
  }, []);

  const goToLast = useCallback(() => {
    setCurrentSlide(slides.length - 1);
    setIsPlaying(false);
  }, [slides.length]);

  // ===== FULLSCREEN TOGGLE (MUST BE BEFORE KEYBOARD NAVIGATION) =====
  const toggleFullscreen = useCallback(() => {
    const elem = document.querySelector('.slider-container');
    
    if (!document.fullscreenElement) {
      elem?.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error('Fullscreen error:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  }, []);

  // ===== TOUCH/SWIPE SUPPORT =====
  const handleTouchStart = useCallback((e) => {
    setTouchStart(e.targetTouches[0].clientX);
  }, []);

  const handleTouchMove = useCallback((e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchStart - touchEnd > 75) {
      // Swiped left
      nextSlide();
    }
    if (touchStart - touchEnd < -75) {
      // Swiped right
      prevSlide();
    }
  }, [touchStart, touchEnd, nextSlide, prevSlide]);

  // ===== TRACK SLIDE VIEWS =====
  useEffect(() => {
    if (slides.length > 0 && slides[currentSlide]) {
      const slideId = slides[currentSlide].id;
      setViewCounts(prev => {
        const updated = {
          ...prev,
          [slideId]: (prev[slideId] || 0) + 1
        };
        // Save to localStorage
        localStorage.setItem(`slider_views_${tenant}`, JSON.stringify(updated));
        return updated;
      });
    }
  }, [currentSlide, slides, tenant]);

  // ===== AUTO-PLAY TIMER (fixed dependencies) =====
  useEffect(() => {
    if (!isPlaying || slides.length <= 1) return;
    
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, interval);
    
    return () => clearTimeout(timer);
  }, [currentSlide, isPlaying, slides.length, interval]);

  // ===== KEYBOARD NAVIGATION (fixed dependencies) =====
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'Home') goToFirst();
      if (e.key === 'End') goToLast();
      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        setIsPlaying(prev => !prev);
      }
      if (e.key === 'Escape' && isFullscreen) {
        toggleFullscreen();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide, goToFirst, goToLast, isFullscreen, toggleFullscreen]);

  // ===== TRANSITION CLASSES =====
  const getTransitionClass = useCallback((index) => {
    const isActive = index === currentSlide;
    
    switch (transitionStyle) {
      case 'fade':
        return `transition-opacity duration-1000 ${isActive ? 'opacity-100 z-10' : 'opacity-0 z-0'}`;
      case 'slide':
        return `transition-transform duration-700 ${
          isActive ? 'translate-x-0 z-10' : 
          index < currentSlide ? '-translate-x-full z-0' : 'translate-x-full z-0'
        }`;
      case 'zoom':
        return `transition-all duration-1000 ${
          isActive ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-95 z-0'
        }`;
      case 'blur':
        return `transition-all duration-1000 ${
          isActive ? 'opacity-100 blur-none z-10' : 'opacity-0 blur-md z-0'
        }`;
      default:
        return `transition-opacity duration-1000 ${isActive ? 'opacity-100 z-10' : 'opacity-0 z-0'}`;
    }
  }, [currentSlide, transitionStyle]);

  // ===== LOADING STATE =====
  if (loading) {
    return (
      <section className="relative w-full h-[500px] lg:h-[600px] bg-slate-900 flex items-center justify-center">
        <div className="inline-block w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        <p className="ml-3 text-white">Loading slideshow...</p>
      </section>
    );
  }

  // ===== NO SLIDES STATE =====
  if (!slides || slides.length === 0) {
    return null;
  }

  const currentSlideData = slides[currentSlide];

  return (
    <section 
      className="slider-container relative w-full h-[500px] lg:h-[600px] overflow-hidden bg-slate-900"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slides */}
      {slides.map((slide, index) => (
        <div 
          key={slide.id} 
          className={`absolute inset-0 ${getTransitionClass(index)}`}
          role="group"
          aria-roledescription="slide"
          aria-label={`Slide ${index + 1} of ${slides.length}`}
          aria-hidden={index !== currentSlide}
        >
          {/* Media (Video or Image) */}
          {slide.type === 'video' ? (
            <video 
              src={slide.media} 
              autoPlay 
              muted 
              loop 
              playsInline
              className="absolute inset-0 w-full h-full object-cover" 
              aria-label={slide.title}
            />
          ) : (
            <div 
              className="absolute inset-0 w-full h-full bg-cover bg-center" 
              style={{ 
                backgroundImage: `url(${slide.media})`, 
                backgroundColor: '#1e293b' 
              }}
              role="img"
              aria-label={slide.title}
            />
          )}
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
          
          {/* Content */}
          <div className="relative h-full flex items-center">
            <div className="container mx-auto px-4">
              <div className="max-w-2xl">
                {/* Badge */}
                {slide.badge && (
                  <span className="inline-block px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold mb-4 animate-pulse">
                    {slide.badge}
                  </span>
                )}
                
                {/* Title */}
                <h2 className="text-4xl lg:text-6xl font-extrabold text-white mb-4 leading-tight drop-shadow-lg">
                  {slide.title}
                </h2>
                
                {/* Description */}
                <p className="text-lg lg:text-xl text-white/90 mb-6 drop-shadow-md">
                  {slide.description}
                </p>
                
                {/* CTA Button */}
                {slide.cta && (
                  <a 
                    href={slide.cta.link} 
                    className="inline-block px-6 py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                  >
                    {slide.cta.text}
                  </a>
                )}
                
                {/* View Count Badge */}
                {viewCounts[slide.id] > 1 && (
                  <div className="mt-4 inline-block px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white text-xs">
                    👁️ Viewed {viewCounts[slide.id]} times
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Progress Bar */}
      {isPlaying && slides.length > 1 && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-white/20 z-30">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all"
            style={{ 
              width: `${((currentSlide + 1) / slides.length) * 100}%` 
            }}
            role="progressbar"
            aria-valuenow={currentSlide + 1}
            aria-valuemin="1"
            aria-valuemax={slides.length}
          />
        </div>
      )}

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button 
            onClick={prevSlide} 
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all flex items-center justify-center text-white group"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6 group-hover:scale-110 transition-transform" />
          </button>
          
          <button 
            onClick={nextSlide} 
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all flex items-center justify-center text-white group"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6 group-hover:scale-110 transition-transform" />
          </button>
        </>
      )}

      {/* Slide Indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20 flex gap-2" role="tablist">
          {slides.map((slide, index) => (
            <button 
              key={index} 
              onClick={() => goToSlide(index)} 
              className={`h-3 rounded-full transition-all ${
                index === currentSlide 
                  ? 'bg-white w-8' 
                  : 'bg-white/50 hover:bg-white/75 w-3'
              }`}
              role="tab"
              aria-label={`Go to slide ${index + 1}`}
              aria-selected={index === currentSlide}
              aria-controls={`slide-${index}`}
            />
          ))}
        </div>
      )}

      {/* Control Panel */}
      {slides.length > 1 && (
        <div className="absolute bottom-6 right-6 z-20 flex gap-2">
          {/* Skip to First */}
          <button 
            onClick={goToFirst}
            disabled={currentSlide === 0}
            className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all flex items-center justify-center text-white disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="First slide"
          >
            <SkipBack className="w-5 h-5" />
          </button>

          {/* Play/Pause Button */}
          <button 
            onClick={() => setIsPlaying(!isPlaying)} 
            className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all flex items-center justify-center text-white"
            aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
          >
            {isPlaying ? (
              <Pause className="w-5 h-5" />
            ) : (
              <Play className="w-5 h-5 ml-0.5" />
            )}
          </button>

          {/* Skip to Last */}
          <button 
            onClick={goToLast}
            disabled={currentSlide === slides.length - 1}
            className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all flex items-center justify-center text-white disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Last slide"
          >
            <SkipForward className="w-5 h-5" />
          </button>

          {/* Fullscreen Button */}
          <button 
            onClick={toggleFullscreen}
            className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all flex items-center justify-center text-white"
            aria-label="Toggle fullscreen"
          >
            <Maximize2 className="w-5 h-5" />
          </button>

          {/* Settings Button */}
          <button 
            onClick={() => setShowSettings(!showSettings)} 
            className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all flex items-center justify-center text-white"
            aria-label="Slideshow settings"
          >
            <Settings className={`w-5 h-5 ${showSettings ? 'animate-spin' : ''}`} />
          </button>
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && (
        <div className="absolute bottom-20 right-6 z-30 bg-white/95 backdrop-blur-md rounded-xl shadow-2xl p-4 min-w-[280px] animate-slideIn">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-slate-900">Slideshow Settings</h3>
            <button
              onClick={() => setShowSettings(false)}
              className="text-slate-500 hover:text-slate-700"
            >
              ×
            </button>
          </div>
          
          {/* Speed Control */}
          <div className="mb-4">
            <label className="block text-sm text-slate-700 mb-2">
              Speed: {interval / 1000}s per slide
            </label>
            <input
              type="range"
              min="2000"
              max="10000"
              step="1000"
              value={interval}
              onChange={(e) => setIntervalDuration(Number(e.target.value))}
              className="w-full accent-blue-600"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>Fast (2s)</span>
              <span>Slow (10s)</span>
            </div>
          </div>

          {/* Transition Style */}
          <div className="mb-4">
            <label className="block text-sm text-slate-700 mb-2">
              Transition Effect
            </label>
            <select
              value={transitionStyle}
              onChange={(e) => setTransitionStyle(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="fade">Fade</option>
              <option value="slide">Slide</option>
              <option value="zoom">Zoom</option>
              <option value="blur">Blur</option>
            </select>
          </div>

          {/* Current Slide Info */}
          <div className="pt-4 border-t border-slate-200">
            <div className="text-xs text-slate-600 space-y-1">
              <p className="flex justify-between">
                <span>Current slide:</span>
                <span className="font-semibold">{currentSlide + 1} of {slides.length}</span>
              </p>
              {currentSlideData?.title && (
                <p className="text-slate-500 truncate" title={currentSlideData.title}>
                  {currentSlideData.title}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Keyboard Hints */}
      <div className="absolute bottom-6 left-6 z-20 text-white/60 text-xs backdrop-blur-sm bg-black/20 px-3 py-2 rounded-lg">
        <p>⌨️ Keyboard shortcuts:</p>
        <p>← → : Navigate | Space: Play/Pause | Home/End: First/Last</p>
      </div>

      {/* Slide Counter (Top Right) */}
      <div className="absolute top-6 right-6 z-20 bg-black/50 backdrop-blur-sm text-white px-3 py-2 rounded-lg text-sm font-semibold">
        {currentSlide + 1} / {slides.length}
      </div>
    </section>
  );
};

export default Slider;